<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import BotsAPI from 'dashboard/api/bots';

const emit = defineEmits(['close', 'imported']);
const router = useRouter();
const route = useRoute();

const fileName = ref('');
const fileSize = ref(0);
const jsonData = ref(null);
const preview = ref(null);
const loading = ref(false);
const importing = ref(false);
const error = ref(null);
const selectedStepIdx = ref(0);

const compatibilityPercent = computed(() => {
  if (!preview.value) return 0;
  const total = preview.value.total_blocks || 1;
  const subbots = preview.value.summary?.subbots || 0;
  return Math.round(((total - subbots) / total) * 100);
});

const selectedStep = computed(() => preview.value?.steps?.[selectedStepIdx.value] || null);

function bytesPretty(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

async function handleFileSelect(file) {
  if (!file) return;
  fileName.value = file.name;
  fileSize.value = file.size;
  error.value = null;
  try {
    const text = await file.text();
    jsonData.value = JSON.parse(text);
    await loadPreview();
  } catch (e) {
    error.value = `Archivo inválido: ${e.message}`;
    jsonData.value = null;
    preview.value = null;
  }
}

function onFileInput(ev) {
  handleFileSelect(ev.target.files[0]);
}

function onDrop(ev) {
  ev.preventDefault();
  handleFileSelect(ev.dataTransfer.files[0]);
}

async function loadPreview() {
  if (!jsonData.value) return;
  loading.value = true;
  try {
    const { data } = await BotsAPI.importKommo({ json_data: jsonData.value, dry_run: true });
    preview.value = data.preview;
    selectedStepIdx.value = 0;
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
    preview.value = null;
  } finally {
    loading.value = false;
  }
}

async function confirmImport() {
  if (!jsonData.value) return;
  importing.value = true;
  try {
    const { data } = await BotsAPI.importKommo({ json_data: jsonData.value, dry_run: false });
    emit('imported', data);
    if (data.id) {
      router.push({ name: 'salesbots_editor', params: { ...route.params, botId: data.id } });
    }
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    importing.value = false;
  }
}

function clearFile() {
  fileName.value = '';
  fileSize.value = 0;
  jsonData.value = null;
  preview.value = null;
  error.value = null;
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    @click.self="emit('close')"
  >
    <div class="bg-n-background border border-n-weak rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="px-5 py-4 border-b border-n-weak flex items-center justify-between shrink-0">
        <div>
          <div class="text-sm font-semibold text-n-slate-12">Importar bot de Kommo</div>
          <div v-if="preview" class="text-[11px] text-n-slate-11 mt-0.5 font-mono">{{ fileName }} · {{ preview.total_blocks }} pasos · {{ compatibilityPercent }}% compatible</div>
          <div v-else-if="fileName" class="text-[11px] text-n-slate-11 mt-0.5 font-mono">{{ fileName }} · {{ bytesPretty(fileSize) }}</div>
        </div>
        <button @click="emit('close')" class="text-n-slate-11 hover:text-n-slate-12 text-xl leading-none">×</button>
      </div>

      <!-- Sin archivo cargado: dropzone -->
      <div v-if="!preview && !loading" class="p-6">
        <label
          @drop="onDrop"
          @dragover.prevent
          class="block bg-blue-500/5 border-2 border-blue-500/30 border-dashed rounded-xl p-10 text-center cursor-pointer hover:bg-blue-500/10 transition"
        >
          <input type="file" accept=".json,application/json" class="hidden" @change="onFileInput" />
          <div class="text-4xl mb-3">📥</div>
          <div class="text-sm font-medium text-n-slate-12">Arrastra el archivo .json aquí</div>
          <div class="text-xs text-n-slate-11 mt-1">o haz click para seleccionarlo</div>
          <div class="text-[10px] text-n-slate-11 mt-3 font-mono">Acepta archivos exportados desde Kommo CRM</div>
        </label>
        <div v-if="error" class="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-300">{{ error }}</div>
      </div>

      <!-- Cargando preview -->
      <div v-else-if="loading" class="p-12 text-center text-sm text-n-slate-11">Analizando archivo…</div>

      <!-- Preview cargado -->
      <div v-else class="flex-1 overflow-hidden flex flex-col">
        <!-- Resumen + warnings -->
        <div class="px-5 py-3 border-b border-n-weak shrink-0 space-y-2">
          <div class="grid grid-cols-5 gap-2 text-center">
            <div class="bg-emerald-500/10 rounded p-1.5"><div class="text-base font-bold text-emerald-400">{{ preview.summary?.messages || 0 }}</div><div class="text-[9px] text-n-slate-11 uppercase">Mensajes</div></div>
            <div class="bg-amber-500/10 rounded p-1.5"><div class="text-base font-bold text-amber-400">{{ preview.summary?.conditions || 0 }}</div><div class="text-[9px] text-n-slate-11 uppercase">Condic.</div></div>
            <div class="bg-violet-500/10 rounded p-1.5"><div class="text-base font-bold text-violet-400">{{ preview.summary?.actions || 0 }}</div><div class="text-[9px] text-n-slate-11 uppercase">Acciones</div></div>
            <div class="bg-rose-500/10 rounded p-1.5"><div class="text-base font-bold text-rose-400">{{ preview.summary?.subbots || 0 }}</div><div class="text-[9px] text-n-slate-11 uppercase">⚠ Sub-bots</div></div>
            <div class="bg-slate-500/10 rounded p-1.5"><div class="text-base font-bold text-n-slate-12">{{ preview.summary?.finishes || 0 }}</div><div class="text-[9px] text-n-slate-11 uppercase">Finish</div></div>
          </div>
          <div v-for="(w, i) in (preview.warnings || [])" :key="i" class="text-[11px] px-2 py-1.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">⚠ {{ w.message }}</div>
        </div>

        <!-- Lista + detalle -->
        <div class="flex-1 grid grid-cols-2 overflow-hidden">
          <!-- Lista de pasos -->
          <div class="border-r border-n-weak overflow-y-auto p-2 space-y-1">
            <button
              v-for="(s, i) in (preview.steps || [])"
              :key="`${s.uuid}-${i}`"
              @click="selectedStepIdx = i"
              class="w-full text-left rounded-md p-2 transition border"
              :class="selectedStepIdx === i ? 'bg-blue-500/10 border-blue-500/40' : 'border-transparent hover:bg-n-alpha-1'"
            >
              <div class="flex items-center gap-1.5 text-[10px]">
                <span class="font-mono text-n-slate-11">#{{ s.step_id }}</span>
                <span>{{ s.icon }}</span>
                <span
                  class="px-1.5 py-0.5 rounded text-[9px] font-medium"
                  :class="{
                    'bg-emerald-500/20 text-emerald-300': s.type === 'message',
                    'bg-amber-500/20 text-amber-300': s.type === 'condition',
                    'bg-violet-500/20 text-violet-300': s.type === 'action',
                    'bg-rose-500/20 text-rose-300': s.type === 'subbot',
                  }"
                >{{ s.type }}</span>
              </div>
              <div class="text-[11px] text-n-slate-12 mt-1 truncate">{{ s.preview_text || s.label }}</div>
            </button>
          </div>

          <!-- Detalle del paso -->
          <div class="overflow-y-auto p-3 text-xs" v-if="selectedStep">
            <div class="text-[10px] uppercase tracking-wider text-n-slate-11 mb-2">Detalle · paso #{{ selectedStep.step_id }}</div>
            <div class="bg-n-alpha-1 border border-n-weak rounded-lg p-3">
              <div class="flex items-center gap-2 mb-2">
                <span>{{ selectedStep.icon }}</span>
                <span class="font-semibold text-n-slate-12">{{ selectedStep.label }}</span>
              </div>
              <div v-if="selectedStep.full_text" class="text-[11px] text-n-slate-11 whitespace-pre-wrap bg-n-background/50 rounded p-2 max-h-48 overflow-y-auto font-mono">{{ selectedStep.full_text }}</div>
              <div v-if="selectedStep.waits_answer" class="mt-2 text-[10px] text-blue-400">⏸ Espera respuesta del cliente</div>
              <div v-if="selectedStep.branches" class="mt-2 space-y-1">
                <div v-for="(b, j) in selectedStep.branches" :key="j" class="text-[11px] bg-amber-500/5 rounded px-2 py-1">
                  <span class="font-mono text-amber-300">{{ b.condition }}</span>
                  <span class="text-n-slate-11"> → paso #{{ b.goto_step }}</span>
                </div>
              </div>
              <div v-if="selectedStep.tags" class="mt-2 flex flex-wrap gap-1">
                <span v-for="t in selectedStep.tags" :key="t" class="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">🏷️ {{ t }}</span>
              </div>
              <div v-if="selectedStep.placeholder" class="mt-2 text-[10px] text-rose-400">⚠ Placeholder · sub-bot externo Kommo #{{ selectedStep.external_kommo_id }} · necesita configuración manual</div>
              <div v-if="selectedStep.pending_attachments && selectedStep.pending_attachments.length" class="mt-2 space-y-1">
                <div v-for="(p, k) in selectedStep.pending_attachments" :key="k" class="text-[10px] text-rose-400 bg-rose-500/5 rounded px-2 py-1">
                  📎 {{ p.name || (p.kind === 'image' ? '(imagen sin nombre)' : '(archivo sin nombre)') }} · súbelo manualmente al paso después del import
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-n-weak flex items-center justify-between shrink-0">
          <button @click="clearFile" class="text-[11px] text-n-slate-11 hover:text-n-slate-12">← Cambiar archivo</button>
          <div class="flex gap-2">
            <button @click="emit('close')" class="text-[11px] text-n-slate-11 px-3 py-1.5">Cancelar</button>
            <button @click="confirmImport" :disabled="importing" class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[12px] font-medium px-4 py-1.5 rounded">
              {{ importing ? 'Importando…' : `Importar bot (${preview.total_blocks} pasos)` }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
